import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, Upload, FileDown, BarChart3, ListChecks, Library, Menu, X, Settings as SettingsIcon, Users as UsersIcon, TrendingUp, Lock,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/lib/auth";
import { usePlan } from "@/lib/plan";
import { Button } from "@/components/ui/button";

const BASE_NAV_ITEMS = [
  { to: "/dashboard",     icon: LayoutDashboard, label: "Dashboard",   testId: "nav-dashboard" },
  { to: "/ar-over-time",  icon: TrendingUp,      label: "AR Trends",   testId: "nav-ar-over-time", feature: "ar_over_time" },
  { to: "/scorecards",    icon: BarChart3,       label: "Scorecards",  testId: "nav-scorecards",   feature: "payor_scorecards" },
  { to: "/workqueue",     icon: ListChecks,      label: "Work Queue",  testId: "nav-workqueue" },
  { to: "/ansi-codes",    icon: Library,         label: "ANSI Codes",  testId: "nav-ansi" },
];
const WRITE_NAV_ITEMS = [
  { to: "/upload",     icon: Upload,          label: "Upload",      testId: "nav-upload" },
  { to: "/exports",    icon: FileDown,        label: "Exports",     testId: "nav-exports" },
];
const ADMIN_NAV_ITEM = { to: "/admin/users", icon: UsersIcon, label: "Admin", testId: "nav-admin" };

const NavLink = ({ to, icon: Icon, label, testId, onClick, theme = "light", locked = false }) => {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== "/" && pathname.startsWith(to));
  const cls = theme === "dark"
    ? (active
        ? "bg-white/15 text-white shadow-sm"
        : "text-slate-200 hover:text-white hover:bg-white/10")
    : (active
        ? "bg-teal-50 text-teal-700 shadow-sm"
        : "text-slate-700 hover:text-teal-700 hover:bg-slate-50");
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testId}
      title={locked ? `${label} — Upgrade to Professional` : label}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${cls} ${locked ? "opacity-80" : ""}`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span>{label}</span>
      {locked ? <Lock className="h-3 w-3 flex-shrink-0 opacity-70" data-testid={`${testId}-lock`} /> : null}
    </Link>
  );
};

export const AppShell = ({ children, theme = "dark", headerTheme = "dark" }) => {
  const { user, logout } = useAuth();
  const { can } = usePlan();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  const isDark = theme === "dark";
  const isLightHeader = headerTheme === "light";
  // Header theming is decoupled from body theming: marketing-style white nav,
  // dark "operating room" body underneath.
  const isHeaderDark = !isLightHeader && isDark;

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = () => {
    setMobileOpen(false);
    logout();
    navigate("/login");
  };

  // Build the nav based on the user's role + plan capabilities.
  // - read_only: hide Upload + Exports entirely (no write capabilities)
  // - admin:     append Admin link at the end
  // Tier-gated items stay visible but render with a 🔒 icon when locked.
  const NAV_ITEMS = [
    ...BASE_NAV_ITEMS.map((item) => ({
      ...item,
      locked: item.feature ? !can(item.feature) : false,
    })),
    ...(user?.role === "read_only" ? [] : WRITE_NAV_ITEMS),
    ...(user?.role === "admin" ? [ADMIN_NAV_ITEM] : []),
  ];

  const headerBg = isHeaderDark
    ? "bg-gradient-to-r from-[#0A1626] via-[#0F2238] to-[#0F4C81] border-b border-white/10"
    : "bg-white/95 backdrop-blur border-b border-slate-200";

  return (
    <div className={`min-h-screen overflow-x-hidden ${isDark ? "dark-theme" : "bg-[#F8FAFC]"}`}>
      <header
        data-testid="app-header"
        className={`sticky top-0 z-40 ${headerBg}`}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center gap-3 sm:gap-6">
          {/* Logo (always visible, never clipped, no duplicate text — the PNG already contains the wordmark) */}
          <Link to="/dashboard" className="flex items-center flex-shrink-0 py-1.5" data-testid="header-logo-link" aria-label="Revenue Cycle IQ AI — Dashboard">
            <BrandLogo variant="header" theme={isHeaderDark ? "dark" : "light"} />
          </Link>

          {/* Desktop nav (≥lg) */}
          <nav className="hidden lg:flex items-center gap-1 flex-1 min-w-0" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) => <NavLink key={item.to} {...item} theme={isHeaderDark ? "dark" : "light"} />)}
          </nav>

          {/* Spacer to push right cluster on tablet (md..lg) */}
          <div className="hidden md:flex lg:hidden flex-1" />

          {/* Tablet nav (md..lg): icons-only */}
          <nav className="hidden md:flex lg:hidden items-center gap-0.5" data-testid="tablet-nav">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = pathname === item.to || pathname.startsWith(item.to);
              const iconCls = isHeaderDark
                ? (active ? "bg-white/15 text-white" : "text-slate-200 hover:text-white hover:bg-white/10")
                : (active ? "bg-teal-50 text-teal-700" : "text-slate-600 hover:text-teal-700 hover:bg-slate-50");
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`${item.testId}-icon`}
                  title={item.label}
                  className={`p-2 rounded-lg transition-all ${iconCls}`}
                >
                  <Icon className="h-4 w-4" />
                </Link>
              );
            })}
          </nav>

          {/* Right cluster */}
          <div className="ml-auto flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* User info — visible md+ */}
            <div className="hidden md:flex flex-col items-end leading-tight max-w-[160px] lg:max-w-[200px]">
              <div className={`text-sm font-medium truncate w-full text-right ${isHeaderDark ? "text-white" : "text-slate-900"}`} data-testid="header-user-name">
                {user?.name || "User"}
              </div>
              <div className={`text-xs truncate w-full text-right ${isHeaderDark ? "text-slate-300" : "text-slate-500"}`} data-testid="header-user-email">
                {user?.email}
              </div>
            </div>
            {/* Avatar */}
            <div
              className={`h-9 w-9 rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${
                isHeaderDark ? "bg-white/15 text-white border border-white/20" : "bg-teal-500 text-slate-900"
              }`}
              data-testid="header-avatar"
              title={user?.name}
            >
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            {/* Logout — visible md+ */}
            <Button
              variant="ghost"
              size="sm"
              data-testid="settings-button"
              onClick={() => navigate("/settings")}
              className={`hidden md:inline-flex ${isHeaderDark ? "text-slate-200 hover:text-white hover:bg-white/10" : "text-slate-600 hover:text-teal-700"}`}
              title="Account settings"
              aria-label="Account settings"
            >
              <SettingsIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-button"
              onClick={handleLogout}
              className={`hidden md:inline-flex ${isHeaderDark ? "text-slate-200 hover:text-white hover:bg-white/10" : "text-slate-600 hover:text-teal-700"}`}
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
            {/* Hamburger — visible <md */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((o) => !o)}
              className={`md:hidden h-9 w-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
                isHeaderDark ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"
              }`}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {mobileOpen && (
          <>
            <div
              className="md:hidden fixed inset-0 top-20 bg-slate-900/30 backdrop-blur-sm z-30"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <div
              className="md:hidden absolute top-20 inset-x-0 bg-white border-b border-slate-200 shadow-xl z-40 fade-up"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-sm font-medium text-slate-900">{user?.name || "User"}</div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
              <nav className="px-2 py-2 space-y-0.5">
                {NAV_ITEMS.map((item) => (
                  <NavLink key={item.to} {...item} onClick={() => setMobileOpen(false)} testId={`${item.testId}-mobile`} />
                ))}
              </nav>
              <div className="px-2 pb-3 pt-1 border-t border-slate-100">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="settings-button-mobile"
                  onClick={() => { setMobileOpen(false); navigate("/settings"); }}
                  className="w-full justify-start text-slate-600 hover:text-navy"
                >
                  <SettingsIcon className="h-4 w-4 mr-2" /> Settings
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="logout-button-mobile"
                  onClick={handleLogout}
                  className="w-full justify-start text-slate-600 hover:text-navy"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </Button>
              </div>
            </div>
          </>
        )}
      </header>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">{children}</main>

      <footer className={`max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-6 text-xs flex flex-col sm:flex-row sm:justify-between gap-2 mt-4 ${isDark ? "border-t border-white/10 text-slate-400" : "border-t border-slate-200 text-slate-500"}`}>
        <div>Revenue Cycle IQ · Denial Intelligence & Recovery Workflow Platform · De-identified Operational Analytics Only</div>
        <div>No PHI is stored or processed.</div>
      </footer>
    </div>
  );
};
